import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, ChevronRight, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { useAuth, startGoogleLogin } from "../lib/auth";

const DISMISS_KEY = "boardroom_signin_prompt_dismissed";

/**
 * Sign-in nudge shown before starting a new session. Signed-in users skip
 * the prompt entirely. Signed-out users see it once per session (unless
 * they choose "Don't ask again" — persisted in localStorage).
 *
 * Renderless component: exposes an `open()` method via ref. Also renders
 * the primary "New session" button so callers can drop in one component.
 *
 * Prop: `children` — the button content. Rendered inside a Button element.
 * Prop: `className` — merged into the Button.
 */
export default function StartSessionPrompt({ children, className = "", testId = "start-session-guarded" }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleClick = (e) => {
    e?.preventDefault?.();
    if (loading) return;
    // Signed in — go straight to the new-session flow.
    if (user) {
      navigate("/meetings/new");
      return;
    }
    // Previously dismissed the prompt → skip nagging.
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") {
        navigate("/meetings/new");
        return;
      }
    } catch (e) {
      // Privacy-mode Safari throws on localStorage — fall through to showing the prompt.
      console.debug("StartSessionPrompt: localStorage read failed", e);
    }
    setOpen(true);
  };

  const proceedAnonymously = () => {
    setOpen(false);
    navigate("/meetings/new");
  };

  const dontAskAgain = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch (e) {
      console.debug("StartSessionPrompt: localStorage write failed", e);
    }
    setOpen(false);
    navigate("/meetings/new");
  };

  return (
    <>
      <Button
        onClick={handleClick}
        className={className || "bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium rounded-md h-11 px-5"}
        data-testid={testId}
      >
        {children || "Call your first session"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-[#0a0a0a] border border-[#2a2a2a] text-[#fafafa] max-w-md"
          data-testid="signin-prompt-dialog"
        >
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={16} className="text-[#c89f45]" />
              <p className="label-eyebrow">Before we start</p>
            </div>
            <DialogTitle className="font-serif text-2xl">
              Sign in so this session sticks with you.
            </DialogTitle>
            <DialogDescription className="text-[#888] pt-3 leading-relaxed">
              Recordings and minutes tied to an anonymous browser can be lost when you clear cookies, switch devices, or sign out.
              <span className="block mt-2 text-[#bbb]">
                Sign in with Google (30 seconds) and this session — plus every session after — will follow you across every device.
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 space-y-2 text-xs text-[#888]" data-testid="signin-prompt-benefits">
            <p className="flex items-start gap-2"><ChevronRight size={12} className="text-[#c89f45] mt-0.5 shrink-0" /> Recordings never orphan to a lost anonymous ID.</p>
            <p className="flex items-start gap-2"><ChevronRight size={12} className="text-[#c89f45] mt-0.5 shrink-0" /> Ask the Boardroom across every past transcript.</p>
            <p className="flex items-start gap-2"><ChevronRight size={12} className="text-[#c89f45] mt-0.5 shrink-0" /> Weekly digest emails hit your inbox on Sundays.</p>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between sm:items-center pt-4">
            <button
              type="button"
              onClick={dontAskAgain}
              className="text-[10px] font-mono uppercase tracking-widest text-[#666] hover:text-[#e07b6a] transition-colors self-start sm:self-auto"
              data-testid="signin-prompt-dont-ask"
            >
              Don't ask again · continue anonymously
            </button>
            <div className="flex gap-2 flex-wrap justify-end">
              <Button
                variant="outline"
                onClick={proceedAnonymously}
                className="bg-transparent border-[#2a2a2a] text-[#ddd] hover:bg-[#1a1a1a] rounded-md h-10"
                data-testid="signin-prompt-anon"
              >
                Continue anonymously
              </Button>
              <Button
                onClick={() => startGoogleLogin()}
                className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium rounded-md h-10"
                data-testid="signin-prompt-signin"
              >
                <LogIn size={14} className="mr-1.5" />
                Sign in with Google
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
